<template>
    <div class="tgp">
        <div class="tgp-head">
            <span class="dash-panel-eyebrow">감축 목표</span>
            <div v-if="target" class="tgp-head-right">
                <span class="tgp-year">{{ pathwayYearLabel }}년 연간 배출량</span>
                <span :class="['tgp-pill', statusWarn ? 'tgp-pill--warn' : '']">{{ statusLabel }}</span>
            </div>
        </div>

        <div v-if="target" class="tgp-body">
            <div :class="['tgp-chart', strategicTarget ? 'tgp-chart--four' : '']">
                <!-- 기준 -->
                <div class="tgp-col">
                    <div class="tgp-col-info">
                        <div class="tgp-col-value">
                            <span class="num tgp-col-emission">{{ fmtN(target.baseEmissions) }}</span>
                            <span class="tgp-col-unit">{{ unitShort }}</span>
                        </div>
                        <span class="tgp-col-rate tgp-col-rate--phantom" aria-hidden="true">—</span>
                    </div>
                    <div class="tgp-bar-area">
                        <div class="tgp-bar tgp-bar--base" style="height: 100%"></div>
                    </div>
                    <div class="tgp-col-footer">
                        <span class="tgp-col-tag">기준연도</span>
                        <span class="tgp-col-yr">({{ target.baseYear }})</span>
                    </div>
                </div>

                <!-- 올해 예상 -->
                <div class="tgp-col">
                    <div class="tgp-col-info">
                        <div class="tgp-col-value">
                            <span class="num tgp-col-emission">{{ fmtN(compareEmission) }}</span>
                            <span class="tgp-col-unit">{{ unitShort }}</span>
                        </div>
                        <span
                            :class="[
                                'num tgp-col-rate',
                                forecastReductionDisplay.variant === 'increased'
                                    ? 'tgp-warn'
                                    : forecastReductionDisplay.variant === 'reduced'
                                      ? 'tgp-good'
                                      : '',
                            ]"
                            :aria-label="forecastReductionDisplay.label"
                        >
                            (<span v-if="forecastReductionDisplay.icon" class="tgp-rate-icon" aria-hidden="true">{{
                                forecastReductionDisplay.icon
                            }}</span
                            >{{ forecastReductionDisplay.text }})
                        </span>
                    </div>
                    <div class="tgp-bar-area">
                        <div
                            :class="['tgp-bar', overshoot > 0 ? 'tgp-bar--warn' : 'tgp-bar--ok']"
                            :style="{ height: compareHeight + '%' }"
                        >
                            <div v-if="adjBands.length" class="tgp-bar-adj-stack">
                                <div
                                    v-for="seg in adjBands"
                                    :key="seg.key"
                                    :class="['tgp-bar-adj-seg', 'tgp-bar-adj-seg--' + seg.key]"
                                    :style="{ height: seg.pct + '%' }"
                                ></div>
                            </div>
                        </div>
                    </div>
                    <div class="tgp-col-footer">
                        <span :class="['tgp-col-tag', overshoot > 0 ? 'tgp-col-tag--warn' : '']">{{
                            compareLabel
                        }}</span>

                        <span class="tgp-col-yr">({{ pathwayYearLabel }})</span>
                    </div>
                </div>

                <!-- 목표 -->
                <div class="tgp-col tgp-col--goal">
                    <div class="tgp-col-info">
                        <div class="tgp-col-value">
                            <span class="num tgp-col-emission">{{ fmtN(goalAmount) }}</span>
                            <span class="tgp-col-unit">{{ unitShort }}</span>
                        </div>
                        <span class="num tgp-col-rate tgp-accent" :aria-label="targetReductionDisplay.label">
                            (<span v-if="targetReductionDisplay.icon" class="tgp-rate-icon" aria-hidden="true">{{
                                targetReductionDisplay.icon
                            }}</span
                            >{{ targetReductionDisplay.text }})
                        </span>
                    </div>
                    <div class="tgp-bar-area">
                        <div class="tgp-bar tgp-bar--goal" :style="{ height: goalHeight + '%' }"></div>
                    </div>
                    <div class="tgp-col-footer">
                        <span class="tgp-col-tag tgp-col-tag--goal">연간 목표</span>
                        <span class="tgp-col-yr">({{ pathwayYearLabel }})</span>
                    </div>
                </div>

                <!-- 중장기 목표 -->
                <div v-if="strategicTarget" class="tgp-col tgp-col--strategic">
                    <div class="tgp-col-info">
                        <div class="tgp-col-value">
                            <span class="num tgp-col-emission">{{ fmtN(strategicTarget.targetEmissions) }}</span>
                            <span class="tgp-col-unit">{{ unitShort }}</span>
                        </div>
                        <span class="num tgp-col-rate tgp-scope3" :aria-label="strategicReductionDisplay.label">
                            (<span v-if="strategicReductionDisplay.icon" class="tgp-rate-icon" aria-hidden="true">{{
                                strategicReductionDisplay.icon
                            }}</span
                            >{{ strategicReductionDisplay.text }})
                        </span>
                    </div>
                    <div class="tgp-bar-area">
                        <div class="tgp-bar tgp-bar--strategic" :style="{ height: strategicHeight + '%' }"></div>
                    </div>
                    <div class="tgp-col-footer tgp-col-footer--stacked">
                        <span class="tgp-col-tag tgp-col-tag--strategic">중장기 목표</span>
                        <span class="tgp-col-yr">({{ strategicTarget.targetYear }})</span>
                    </div>
                </div>
            </div>

            <!-- 정산 조정 범례: 바 차트 아래, 이행 정산 섹션 위 -->
            <div v-if="adjBands.length" class="tgp-adj-legend">
                <span class="tgp-adj-legend-label">정산 조정</span>
                <span
                    v-for="seg in adjBands"
                    :key="seg.key + '_leg'"
                    :class="['tgp-adj-legend-item', 'tgp-adj-legend-item--' + seg.key]"
                >
                    <span class="tgp-adj-legend-swatch"></span>
                    <span class="tgp-adj-legend-name">{{ seg.label }}</span>
                    <span class="tgp-adj-legend-val num"
                        >{{ fmtN(seg.value) }}<span class="tgp-adj-legend-unit">{{ unitShort }}</span></span
                    >
                </span>
            </div>
        </div>

        <div v-else class="tgp-empty">목표 미설정</div>
    </div>
</template>

<script>
import { toDisplayValue } from "@/submodules/carbon-plugin/utils/formatters";

export default {
    name: "TargetGoalPanel",
    props: {
        target: { type: Object, default: null },
        apiUnit: { type: String, default: "kgCO2e" },
        displayUnit: { type: String, default: "kgCO2e" },
    },
    computed: {
        unitShort() {
            return this.displayUnit === "tCO2e" ? "t" : "kg";
        },
        pathwayYearLabel() {
            return (
                this.target?.pathwayYear ||
                this.target?.complianceFlex?.complianceYear ||
                this.target?.strategicTarget?.targetYear ||
                "-"
            );
        },
        goalAmount() {
            if (!this.target) return 0;
            return this.target.goalAmount ?? this.target.targetEmissions ?? 0;
        },
        compareEmission() {
            if (!this.target) return 0;
            return this.target.forecastValue ?? this.target.operationalValue ?? this.target.actualEmissions ?? 0;
        },
        compareLabel() {
            return this.target?.viewMode == "YEAR" ? "연간 배출량" : "월간 배출량";
        },
        overshoot() {
            if (!this.target) return 0;
            return (this.compareEmission || 0) - this.goalAmount;
        },
        compareHeight() {
            if (!this.target?.baseEmissions) return 0;
            return Math.min(100, (this.compareEmission / this.target.baseEmissions) * 100);
        },
        goalHeight() {
            if (!this.target?.baseEmissions) return 0;
            return Math.min(100, (this.goalAmount / this.target.baseEmissions) * 100);
        },
        statusLabel() {
            const s = this.target?.goalStat;
            if (s === "ACHIEVED") return "달성";
            if (s === "FAILED") return "초과";
            if (s === "AT_RISK") return "초과 예상";
            return "진행 중";
        },
        statusWarn() {
            return ["FAILED", "AT_RISK"].includes(this.target?.goalStat);
        },
        /** baseYearComparison.reductionRate — 양수=감축, 음수=기준 대비 증가 */
        forecastReductionDisplay() {
            return this.formatBaselineDelta(this.target?.reductionRate);
        },
        /** 목표 감축률 — (기준연도 − 목표배출량) / 기준연도 × 100 */
        targetReductionDisplay() {
            const base = Number(this.target?.baseEmissions);
            const goal = this.goalAmount;
            if (!base || base <= 0) return { text: "—", icon: "", label: "목표 감축률 데이터 없음" };
            const rate = Math.round(((base - goal) / base) * 10000) / 100;
            return this.formatBaselineDelta(rate);
        },
        strategicReductionDisplay() {
            const base = Number(this.target?.baseEmissions);
            const goal = Number(this.strategicTarget?.targetEmissions);
            if (!base || base <= 0 || !Number.isFinite(goal)) {
                return { text: "—", icon: "", label: "중장기 목표 감축률 데이터 없음" };
            }
            const rate = Math.round(((base - goal) / base) * 10000) / 100;
            return this.formatBaselineDelta(rate);
        },
        strategicTarget() {
            return this.target?.strategicTarget || null;
        },
        strategicHeight() {
            const base = Number(this.target?.baseEmissions);
            const goal = Number(this.strategicTarget?.targetEmissions);
            if (!base || base <= 0 || !Number.isFinite(goal)) return 0;
            return Math.min(100, (goal / base) * 100);
        },
        complianceFlex() {
            return this.target?.complianceFlex || null;
        },
        grossActual() {
            return this.numberOrNull(this.complianceFlex?.grossActualKg);
        },
        adjustmentEntries() {
            const flex = this.complianceFlex;
            if (!flex) return [];
            return [
                { key: "carry", label: "이월", value: this.numberOrNull(flex.carryInAppliedKg) },
                { key: "borrow", label: "차입", value: this.numberOrNull(flex.borrowAppliedKg) },
                { key: "offset", label: "상쇄", value: this.numberOrNull(flex.offsetAppliedKg) },
                { key: "out", label: "발생 이월", value: this.numberOrNull(flex.carryOutGeneratedKg) },
            ].filter((entry) => entry.value != null && entry.value > 0);
        },
        adjBands() {
            if (!this.adjustmentEntries.length || !this.compareEmission) return [];
            const rawBands = this.adjustmentEntries.map((e) => ({
                ...e,
                pct: (e.value / this.compareEmission) * 100,
            }));
            // 전체 합이 90% 초과 시 비례 축소 (나머지 10%는 순배출 영역 보존)
            const sum = rawBands.reduce((s, b) => s + b.pct, 0);
            const scale = sum > 90 ? 90 / sum : 1;
            return rawBands.map((b) => ({ ...b, pct: Math.max(2, b.pct * scale) }));
        },
    },
    methods: {
        numberOrNull(v) {
            if (v == null || v === "") return null;
            const n = Number(v);
            return Number.isFinite(n) ? n : null;
        },
        /**
         * (기준배출 − 현재) / 기준 × 100 — 양수는 감축률로 표시한다.
         * @returns {{ text: string, icon: string, label: string, variant: 'reduced'|'increased'|'neutral' }}
         */
        formatBaselineDelta(rate) {
            if (rate == null || isNaN(rate)) {
                return { text: "—", icon: "", label: "기준연도 대비 데이터 없음", variant: "neutral" };
            }
            const n = Number(rate);
            if (n > 0) {
                return { text: `${n.toFixed(1)}%`, icon: "▼", label: "기준연도 대비 감축", variant: "reduced" };
            }
            if (n < 0) {
                return {
                    text: `${Math.abs(n).toFixed(1)}%`,
                    icon: "▲",
                    label: "기준연도 대비 증가",
                    variant: "increased",
                };
            }
            return { text: "0.0%", icon: "", label: "기준연도 대비 변화 없음", variant: "neutral" };
        },
        fmtN(v) {
            if (v == null || isNaN(v)) return "—";
            const n = toDisplayValue(Number(v), this.apiUnit, this.displayUnit);
            return Math.round(n).toLocaleString("ko-KR");
        },
    },
};
</script>

<style scoped>
.tgp {
    background: var(--dash-surface);
    border: 1px solid var(--dash-hairline-soft);
    border-radius: 14px;
    padding: 14px 18px 12px;
    height: 100%;
    display: flex;
    flex-direction: column;
    min-height: 0;
    min-width: 0;
    color: var(--dash-ink);
}
.num {
    font-variant-numeric: tabular-nums;
    font-feature-settings: "tnum" 1;
}

/* 헤더 */
.tgp-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
    flex-shrink: 0;
}
.tgp-head-right {
    display: flex;
    align-items: center;
    gap: 6px;
}
.tgp-year {
    font-size: 11px;
    color: var(--dash-ink-faint);
    letter-spacing: 0.04em;
}
.tgp-pill {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
    background: var(--dash-accent-wash);
    color: var(--dash-accent);
}
.tgp-pill--warn {
    background: var(--dash-warn-wash);
    color: var(--dash-warn);
}
.tgp-pill--muted {
    background: var(--dash-surface-2);
    color: var(--dash-ink-mute);
}

/* 바디 */
.tgp-body {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

/* 세로 바 차트 */
.tgp-chart {
    flex: 1;
    min-height: 0;
    display: flex;
    gap: 6px;
    align-items: stretch;
}
.tgp-chart--four {
    gap: 5px;
}

/* 각 컬럼 */
.tgp-col {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 0;
    padding: 4px;
}
.tgp-col--goal {
    border-radius: 10px;
    background: var(--dash-accent-wash);
}
.tgp-col--strategic {
    border-radius: 10px;
    background: var(--dash-scope3-wash);
}

/* 상단 수치: 1행 값+단위, 2행 감축률(%) */
.tgp-col-info {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 3px;
    padding: 6px 0 6px;
    text-align: center;
    flex-direction: column;
}
.tgp-col-value {
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 3px;
    white-space: nowrap;
}
.tgp-col-emission {
    font-size: 11px;
    font-weight: 700;
    color: var(--dash-ink);
    line-height: 1.1;
}
.tgp-col-unit {
    font-size: 9px;
    color: var(--dash-ink-faint);
    font-weight: 500;
    letter-spacing: 0.04em;
}
.tgp-col-rate {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: -0.01em;
    line-height: 1.1;
    white-space: normal;
}
.tgp-col-rate--phantom {
    visibility: hidden;
    pointer-events: none;
    user-select: none;
}
.tgp-rate-icon {
    display: inline-block;
    margin-right: 2px;
    font-size: 9px;
    line-height: 1;
    transform: translateY(-1px);
}

/* 바 영역 */
.tgp-bar-area {
    flex: 1;
    width: 60%;
    min-height: 0;
    position: relative;
}
.tgp-bar {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    border-radius: 5px 5px 3px 3px;
    transition: height 0.5s ease;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
.tgp-bar-adj-stack {
    display: flex;
    flex-direction: column;
    width: 100%;
    flex-shrink: 0;
}
.tgp-bar-adj-seg {
    width: 100%;
    min-height: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    transition: height 0.5s ease;
}
.tgp-bar-adj-seg--carry {
    background: oklch(0.58 0.14 155 / 0.72);
}
.tgp-bar-adj-seg--borrow {
    background: oklch(0.62 0.17 28 / 0.72);
}
.tgp-bar-adj-seg--offset {
    background: oklch(0.5 0.12 280 / 0.72);
}
.tgp-bar-adj-seg--out {
    background: oklch(0.5 0.06 220 / 0.6);
}
.tgp-adj-legend {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px 10px;
    padding: 5px 8px;
    border-radius: 8px;
    background: var(--dash-surface-2);
}
.tgp-adj-legend-label {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.06em;
    color: var(--dash-ink-faint);
    text-transform: uppercase;
    flex-shrink: 0;
    margin-right: 2px;
}
.tgp-adj-legend-item {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}
.tgp-adj-legend-swatch {
    width: 7px;
    height: 7px;
    border-radius: 2px;
    flex-shrink: 0;
}
.tgp-adj-legend-item--carry .tgp-adj-legend-swatch {
    background: oklch(0.58 0.14 155);
}
.tgp-adj-legend-item--borrow .tgp-adj-legend-swatch {
    background: oklch(0.62 0.17 28);
}
.tgp-adj-legend-item--offset .tgp-adj-legend-swatch {
    background: oklch(0.5 0.12 280);
}
.tgp-adj-legend-item--out .tgp-adj-legend-swatch {
    background: oklch(0.5 0.06 220);
}
.tgp-adj-legend-name {
    font-size: 9px;
    font-weight: 700;
    color: var(--dash-ink-mute);
}
.tgp-adj-legend-val {
    font-size: 10px;
    font-weight: 800;
    color: var(--dash-ink);
    letter-spacing: -0.01em;
}
.tgp-adj-legend-unit {
    font-size: 8px;
    font-weight: 500;
    color: var(--dash-ink-faint);
    margin-left: 1px;
}
.tgp-bar--base {
    background: var(--dash-base-bar);
}
.tgp-bar--ok {
    background: var(--dash-ok-bar);
}
.tgp-bar--warn {
    background: var(--dash-warn);
    opacity: 0.75;
}
.tgp-bar--goal {
    background: var(--dash-accent);
}
.tgp-bar--strategic {
    background: var(--dash-scope3);
}

/* 하단 레이블 */
.tgp-col-footer {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 1px;
    padding: 5px 0 4px;
}
.tgp-col-footer--stacked {
    flex-wrap: wrap;
    justify-content: center;
}
.tgp-col-yr {
    font-weight: 700;
    color: var(--dash-ink-mute);
    letter-spacing: 0.02em;
    line-height: 1;
    font-size: 10px;
}
.tgp-col-tag {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--dash-ink-faint);
    line-height: 1;
}
.tgp-col-tag--goal {
    color: var(--dash-accent);
}
.tgp-col-tag--strategic {
    color: var(--dash-scope3);
}
.tgp-col-tag--warn {
    color: var(--dash-warn);
}

/* 컬러 유틸 */
.tgp-good {
    color: var(--dash-accent);
}
.tgp-warn {
    color: var(--dash-warn);
}
.tgp-accent {
    color: var(--dash-accent);
}
.tgp-scope3 {
    color: var(--dash-scope3);
}

/* 목표 미설정 */
.tgp-empty {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    color: var(--dash-ink-faint);
    letter-spacing: 0.02em;
}
</style>
